import { useMemo, useState } from 'react';
import type { BillableEventKind, BillingProfileRow, PayeeRateCardRow, PartnerCatalogueRow } from '@dru/types';
import { BILLABLE_EVENT_LABEL, formatPaise } from '@dru/types';
import {
  Alert, Button, ConfirmDialog, DataTable, DetailSection, IconAction, InfoTip, Input, RowActions, SearchSelect, Select, StatusBadge, TextField, useToast,
} from '@dru/ui';
import { BadgeCheck } from 'lucide-react';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage, useAuth } from '../../../lib/auth';
import { formatDate } from '../../../lib/format';
import { holds } from '../../../layouts/ConsoleShell';
import { Dialog } from '../../../components/Dialog';

/**
 * The published card in force today, if there is one. A card is in force
 * from its first day through its last; the open-ended one has no last day.
 */
export function currentRateCard(cards: PayeeRateCardRow[], today = new Date().toISOString().slice(0, 10)): PayeeRateCardRow | null {
  return cards.find((c) => c.status === 'PUBLISHED'
    && c.effectiveFrom.slice(0, 10) <= today
    && (!c.effectiveTo || c.effectiveTo.slice(0, 10) >= today)) ?? null;
}

const CARD_STATUS: Record<string, { label: string; tone: 'covered' | 'pending' | 'neutral' }> = {
  PUBLISHED: { label: 'Published', tone: 'covered' },
  DRAFT: { label: 'Draft', tone: 'pending' },
  SUPERSEDED: { label: 'Superseded', tone: 'neutral' },
};

/**
 * What a venue or a physician is paid, as versioned cards.
 *
 * A card is DRAFT until published, PUBLISHED is frozen, and publishing
 * closes the previous card the day before — so "what did we pay for a CBC on
 * 14 March" has exactly one answer, which is the question every dispute
 * reduces to. There is no edit and no unpublish: a price change is a new
 * card, and the history below is the cards themselves, read per test.
 *
 * Creating and publishing need `settlement.rate-manage`; a reader who only
 * holds `settlement.read` sees the cards and is offered neither. Publishing
 * is confirmed, because it cannot be taken back.
 */
export function PayeeRates({ payeeType = 'PARTNER', payeeId, partnerId, catalogue = [], onChanged }: {
  payeeType?: 'PARTNER' | 'PHYSICIAN';
  payeeId?: string;
  /** @deprecated Pass `payeeId`. Kept so existing partner call sites still read naturally. */
  partnerId?: string;
  catalogue?: PartnerCatalogueRow[];
  /** Told after a card is created or published, so a readiness list above can refresh. */
  onChanged?: () => void;
}) {
  const id = payeeId ?? partnerId ?? '';
  const { permissions } = useAuth();
  const canManage = holds(permissions, 'settlement.rate-manage');
  const { toast } = useToast();
  // A physician is paid per signature, so there is no test catalogue to price
  // against and the per-test history below has nothing to show.
  const perTest = payeeType === 'PARTNER';
  const cards = useQuery<{ items: PayeeRateCardRow[] }>(
    () => api.get(`/settlements/rates/${payeeType}/${id}`), [payeeType, id],
  );
  const [creating, setCreating] = useState(false);
  const [publishing, setPublishing] = useState<PayeeRateCardRow | null>(null);

  const items = cards.data?.items ?? [];
  const inForce = currentRateCard(items);

  // Per test: every published card's price for it, oldest first. A card with
  // a default "any test" line and no line for this test priced it at the
  // default, which is what the venue was actually paid.
  const history = useMemo(() => {
    if (!perTest) return [];
    const published = items
      .filter((c) => c.status !== 'DRAFT')
      .sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom));
    return catalogue.map((t) => ({
      test: t,
      steps: published.map((c) => {
        const line = c.lines.find((l) => l.event === 'TEST_RESULTED' && l.testId === t.id)
          ?? c.lines.find((l) => l.event === 'TEST_RESULTED' && l.testId === null && !l.profileId);
        return { from: c.effectiveFrom, to: c.effectiveTo ?? null, amountPaise: line?.amountPaise ?? null, card: c.name };
      }).filter((s) => s.amountPaise !== null),
    })).filter((h) => h.steps.length > 0);
  }, [items, catalogue, perTest]);

  const create = canManage
    ? <Button size="sm" onClick={() => setCreating(true)}>Create rate card</Button>
    : undefined;

  return (
    <div className="space-y-4">
      <DetailSection title="Rate cards" action={items.length ? create : undefined}>
        <div className="flex justify-end">
          <InfoTip label="About rate cards">
            {perTest
              ? 'What the platform pays this partner per employee attended, per billing profile and per test resulted. A profile is paid once when all its tests are resulted on a visit; a test inside a billed profile is not paid again unless its rate says so. The lab is paid these agreed rates whatever its own list price.'
              : 'What the platform pays this physician per review signed.'}
            {' '}A published card is frozen and prices all work done while it is in force.
          </InfoTip>
        </div>
        {!cards.loading && !cards.error && items.length > 0 && !inForce && (
          <Alert tone="warning" className="mt-3">
            No published card is in force today, so work completed now settles as unpriced.
          </Alert>
        )}
        <div className="mt-3">
          <DataTable<PayeeRateCardRow>
            rows={items}
            loading={cards.loading}
            error={cards.error}
            onRetry={cards.reload}
            rowKey={(c) => c.id}
            empty={{
              title: 'No rate card yet',
              hint: perTest
                ? 'Until one is published, work this partner completes settles as unpriced — counted, never paid at zero.'
                : 'A published rate card is what makes a signed review payable. Without one, reviews settle as unpriced.',
              action: canManage ? <Button onClick={() => setCreating(true)}>Create rate card</Button> : undefined,
            }}
            columns={[
              { key: 'name', header: 'Rate card', alwaysVisible: true, cell: (c) => (
                <div>
                  <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-neutral-900">
                    {c.name}
                    {inForce?.id === c.id && <StatusBadge tone="info">In force</StatusBadge>}
                  </div>
                  <div className="text-xs text-neutral-500">
                    From {formatDate(c.effectiveFrom)}{c.effectiveTo ? ` to ${formatDate(c.effectiveTo)}` : ', open-ended'}
                  </div>
                </div>
              ) },
              { key: 'lines', header: 'Rates', cell: (c) => (
                <ul className="space-y-0.5">
                  {c.lines.map((l) => (
                    <li key={l.id} className="text-xs text-neutral-700">
                      <span className="tabular-nums font-medium">{formatPaise(l.amountPaise, c.currency)}</span>
                      {' · '}{BILLABLE_EVENT_LABEL[l.event]?.toLowerCase() ?? l.event}
                      {l.profile
                        ? <> · profile {l.profile.name} <span className="font-mono text-neutral-500">{l.profile.code}</span>{l.profile._count ? ` (${l.profile._count.tests} tests)` : ''}</>
                        : l.test ? <> · {l.test.name} <span className="font-mono text-neutral-500">{l.test.code}</span></> : l.event === 'TEST_RESULTED' ? ' · any test' : ''}
                      {l.chargeInsideProfile ? ' · also charged inside a profile' : ''}
                    </li>
                  ))}
                </ul>
              ) },
              { key: 'status', header: 'Status', mobileLabel: false, cell: (c) => (
                <StatusBadge tone={CARD_STATUS[c.status]?.tone ?? 'neutral'}>
                  {CARD_STATUS[c.status]?.label ?? c.status}
                </StatusBadge>
              ) },
              { key: 'act', header: 'Actions', align: 'right', alwaysVisible: true, mobileLabel: '', cell: (c) => (
                canManage && c.status === 'DRAFT'
                  ? (
                    <RowActions>
                      <IconAction label={`Publish rate card ${c.name}`} icon={BadgeCheck} primary onClick={() => setPublishing(c)} />
                    </RowActions>
                  )
                  : null
              ) },
            ]}
          />
        </div>
      </DetailSection>

      {perTest && history.length > 0 && (
      <DetailSection title={`Price history by test (${history.length})`} action={(
        <InfoTip label="About the price history">
          What each test has paid across the published cards, oldest first.
        </InfoTip>
      )}>
        <div className="overflow-x-auto rounded border border-neutral-100">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-left text-caption font-medium uppercase tracking-wide text-neutral-500">
              <tr><th scope="col" className="px-3 py-2">Test</th><th scope="col" className="px-3 py-2">Rate over time</th></tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {history.map((h) => (
                <tr key={h.test.id}>
                  <td className="px-3 py-2">
                    <span className="block text-neutral-900">{h.test.name}</span>
                    <span className="block font-mono text-caption text-neutral-500">{h.test.code}</span>
                  </td>
                  <td className="px-3 py-2">
                    <ol className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                      {h.steps.map((s, i) => (
                        <li key={i} className="flex items-center gap-2" title={s.card}>
                          {i > 0 && <span aria-hidden className="text-neutral-400">→</span>}
                          <span className="tabular-nums text-neutral-900">{formatPaise(s.amountPaise!)}</span>
                          <span className="text-neutral-500">from {formatDate(s.from)}{s.to ? ` to ${formatDate(s.to)}` : ''}</span>
                        </li>
                      ))}
                    </ol>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DetailSection>
      )}

      {creating && (
        <NewPayeeCardDialog payeeType={payeeType} payeeId={id} catalogue={catalogue}
          onClose={() => setCreating(false)}
          onDone={() => { setCreating(false); cards.reload(); onChanged?.(); toast('success', 'Rate card saved as a draft. Publish it to put it in force.'); }} />
      )}

      {publishing && (
        <ConfirmDialog
          title={`Publish ${publishing.name}?`}
          what={<>It comes into force on <strong>{formatDate(publishing.effectiveFrom)}</strong> and prices all work from that day.</>}
          affects={inForce && inForce.id !== publishing.id
            ? <>The card in force now, <strong>{inForce.name}</strong>, closes the day before. Statements already closed are not re-priced.</>
            : 'Statements already closed are not re-priced.'}
          irreversible="A published card cannot be edited or unpublished. A price change later is a new card."
          confirmLabel="Publish rate card"
          onConfirm={async () => {
            await api.post(`/settlements/rates/${publishing.id}/publish`, {});
            setPublishing(null);
            cards.reload();
            onChanged?.();
            toast('success', `${publishing.name} published.`);
          }}
          onCancel={() => setPublishing(null)}
        />
      )}
    </div>
  );
}

type Draft = { event: BillableEventKind; testId: string; profileId: string; inside: boolean; rupees: string };

function NewPayeeCardDialog({ payeeType, payeeId, catalogue, onClose, onDone }: {
  payeeType: 'PARTNER' | 'PHYSICIAN'; payeeId: string;
  catalogue: PartnerCatalogueRow[]; onClose: () => void; onDone: () => void;
}) {
  const perTest = payeeType === 'PARTNER';
  const [name, setName] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  // A physician earns on the signature and nothing else, so the card opens on
  // the one line that will actually price their work.
  const [lines, setLines] = useState<Draft[]>(perTest
    ? [
        { event: 'ENCOUNTER_COMPLETED', testId: '', profileId: '', inside: false, rupees: '' },
        { event: 'TEST_RESULTED', testId: '', profileId: '', inside: false, rupees: '' },
      ]
    : [{ event: 'REPORT_REVIEW', testId: '', profileId: '', inside: false, rupees: '' }]);
  const profiles = useQuery<{ items: BillingProfileRow[] }>(
    () => (perTest ? api.get('/billing-profiles') : Promise.resolve({ items: [] })), [perTest],
  );
  const profileList = profiles.data?.items ?? [];
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (i: number, patch: Partial<Draft>) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  const keys = lines.map((l) => `${l.event}:${l.testId}:${l.profileId}`);
  const duplicate = keys.length !== new Set(keys).size;
  const bad = lines.some((l) => !(Number(l.rupees) >= 0 && l.rupees.trim() !== ''));
  const valid = name.trim().length >= 2 && lines.length > 0 && !bad && !duplicate;
  const testName = (id: string) => catalogue.find((t) => t.id === id);

  async function submit() {
    setBusy(true); setError(null);
    try {
      await api.post('/settlements/rates', {
        payeeType, payeeId, name: name.trim(), effectiveFrom,
        notes: notes.trim() || undefined,
        lines: lines.map((l) => ({
          event: l.event,
          ...(l.event === 'TEST_RESULTED' && l.profileId ? { profileId: l.profileId } : {}),
          ...(l.event === 'TEST_RESULTED' && !l.profileId && l.testId ? { testId: l.testId, chargeInsideProfile: l.inside } : {}),
          label: l.event === 'TEST_RESULTED'
            ? (l.profileId
              ? `${profileList.find((p) => p.id === l.profileId)?.name ?? 'Profile'} (profile)`
              : l.testId ? `${testName(l.testId)?.name ?? 'Test'} resulted` : 'Any test resulted')
            : BILLABLE_EVENT_LABEL[l.event],
          amountPaise: Math.round(Number(l.rupees) * 100),
        })),
      }, crypto.randomUUID());
      onDone();
    } catch (e) { setError(errorMessage(e)); setBusy(false); }
  }

  return (
    <Dialog title="Create a rate card" onClose={onClose} wide>
      <div className="space-y-3">
        <p className="text-xs text-neutral-500">
          Saved as a draft. Nothing is paid at these rates until you publish it, and once published it cannot be changed.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <TextField
            label="Name" value={name} onChange={setName} autoFocus required
            placeholder={perTest ? 'e.g. FY 2026-27 agreement' : 'e.g. FY 2026-27 review fees'}
          />
          <TextField
            label="In force from" type="date" value={effectiveFrom} onChange={setEffectiveFrom} required
            hint="Publishing closes the current card the day before."
          />
        </div>
        <div className="space-y-2">
          <div className="text-small font-medium text-neutral-800">Rates</div>
          {lines.map((l, i) => (
            <div key={i} className="grid gap-2 rounded-input border border-neutral-200 p-2 sm:grid-cols-[12rem_1fr_8rem_auto] sm:items-center">
              <Select aria-label="Event" value={l.event} onChange={(e) => set(i, { event: e.target.value as BillableEventKind, testId: '', profileId: '', inside: false })}>
                {(Object.keys(BILLABLE_EVENT_LABEL) as BillableEventKind[])
                  .filter((k) => (perTest ? k !== 'REPORT_REVIEW' : k === 'REPORT_REVIEW'))
                  .map((k) => <option key={k} value={k}>{BILLABLE_EVENT_LABEL[k]}</option>)}
              </Select>
              {l.event === 'TEST_RESULTED' ? (
                <div className="space-y-1">
                  <SearchSelect aria-label="Test or profile" value={l.profileId ? `profile:${l.profileId}` : l.testId}
                    onChange={(v) => (v.startsWith('profile:')
                      ? set(i, { profileId: v.slice(8), testId: '', inside: false })
                      : set(i, { testId: v, profileId: '' }))}
                    placeholder="Any test this venue results" searchPlaceholder="Search tests or profiles…"
                    options={[
                      { value: '', label: 'Any test this venue results' },
                      ...profileList.map((p) => ({ value: `profile:${p.id}`, label: `Profile: ${p.name}`, hint: `${p.code} · ${p.tests.length} tests` })),
                      ...catalogue.map((t) => ({ value: t.id, label: t.name, hint: t.code })),
                    ]} />
                  {l.profileId && (
                    <p className="text-caption text-neutral-500">
                      One price for: {profileList.find((p) => p.id === l.profileId)?.tests.map((t) => t.name).join(', ')}
                    </p>
                  )}
                  {!l.profileId && l.testId && (
                    <label className="flex items-center gap-2 text-caption text-neutral-600">
                      <input type="checkbox" checked={l.inside} onChange={(e) => set(i, { inside: e.target.checked })} />
                      Also charge this test when a billed profile already covers it (only if agreed)
                    </label>
                  )}
                </div>
              ) : <span className="text-xs text-neutral-500">One rate for every such event.</span>}
              <Input aria-label="Rate in rupees" inputMode="decimal" placeholder="₹ 0.00" value={l.rupees}
                onChange={(e) => set(i, { rupees: e.target.value.replace(/[^0-9.]/g, '') })} />
              <Button variant="secondary" size="sm" disabled={lines.length === 1} aria-label={`Remove rate ${i + 1}`} onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}>Remove</Button>
            </div>
          ))}
          <Button variant="secondary" size="sm" onClick={() => setLines((ls) => [...ls, { event: perTest ? 'TEST_RESULTED' : 'REPORT_REVIEW', testId: '', profileId: '', inside: false, rupees: '' }])}>Add a rate</Button>
          {duplicate && <p role="alert" className="text-xs text-danger-600">Two lines price the same thing. Keep one of them.</p>}
        </div>
        <TextField label="Notes (optional)" value={notes} onChange={setNotes} placeholder="Agreement reference" />
        {error && <Alert>{error}</Alert>}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={!valid || busy} loading={busy}>Save as draft</Button>
        </div>
      </div>
    </Dialog>
  );
}
